<script>
  // The Propose Trade button, and its Want Overlap state (VISUALS.md "Want
  // Overlap Pill", spec 2026-09-21). ONE element from first paint: the
  // resting label and the avatar+words share a grid cell and the button keeps
  // its own min-width, so entering the pill state never changes its width.
  // Once `overlap` is set the label exits and the avatar slides in from the
  // right end along a full-width track (a percentage transform, no measured
  // pixels), the words trailing behind. The animations are gated on
  // `.entering`, so they play once per mount and never on later re-renders.
  // transform + opacity only; every duration/delay is a tokens.css custom
  // property (reduced motion collapses them all). The wanter arrives already
  // resolved (name/photo from the groups store) so this stays a pure prop
  // renderer with no store imports.
  import { pillLabel } from '../../lib/logic/want-overlap-logic.js';

  /**
   * @type {{
   *   wanter?: { uid: string, displayName: string | null, photoUrl: string | null } | null,
   *   cardCount?: number,
   *   onclick?: (event: MouseEvent) => void,
   * }}
   */
  let { wanter = null, cardCount = 0, onclick = () => {} } = $props();

  const words = $derived(pillLabel(cardCount).split(' '));
  const initial = $derived((wanter?.displayName || '?').charAt(0).toUpperCase());
  const title = $derived(
    !wanter
      ? undefined
      : wanter.displayName
        ? `${wanter.displayName} wants ${cardCount} of your cards`
        : `Wants ${cardCount} of your cards`
  );
</script>

<button class="propose-trade-button" class:entering={wanter !== null} {onclick} {title}>
  <span class="layer old" aria-hidden={wanter ? 'true' : undefined}>Propose Trade</span>
  {#if wanter}
    <span class="layer track" aria-hidden="true">
      <span class="avatar">
        {#if wanter.photoUrl}
          <img src={wanter.photoUrl} alt="" referrerpolicy="no-referrer" />
        {:else}
          <span class="initial">{initial}</span>
        {/if}
      </span>
    </span>
    <span class="layer new">
      <span class="avatar-gap"></span>
      <span class="text">
        {#each words as word, i (i)}
          <span class="word" style:--i={i}>{word}</span>
        {/each}
      </span>
    </span>
  {/if}
</button>

<style>
  /* Matches the sort-tabs pill height. */
  .propose-trade-button {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    border-radius: 9999px;
    height: calc(38px + 0.6rem);
    min-width: 11.5rem;
    font-weight: 600;
    font-size: 0.95rem;
    line-height: 1;
    cursor: pointer;
    transition:
      transform var(--dur-base),
      box-shadow var(--dur-base);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    display: grid;
    grid-template-areas: 's';
    align-items: center;
    padding: 0 1.1rem 0 6px;
    position: relative;
    overflow: hidden;
  }
  .propose-trade-button:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  .propose-trade-button:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  .layer {
    grid-area: s;
  }
  .old {
    text-align: center;
    padding-left: calc(1.1rem - 6px); /* re-center against the asymmetric padding */
    white-space: nowrap;
  }
  .entering .old {
    will-change: transform;
    animation: pill-exit var(--dur-pill-exit) var(--ease-standard) both;
  }
  /* The avatar's track spans the pill's content box, so the slide's
     percentage start resolves to the pill's right end at every width. */
  .track {
    width: 100%;
    will-change: transform;
    animation: pill-slide var(--dur-pill-enter) var(--ease-standard) both;
  }
  .new {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .avatar,
  .avatar-gap {
    width: 34px;
    height: 34px;
    flex: 0 0 auto;
  }
  .avatar {
    border-radius: 50%;
    overflow: hidden;
    background: #e0e7ff;
    color: #3730a3;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.55);
  }
  .avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .text {
    display: inline-flex;
    gap: 0.3em;
    white-space: nowrap;
  }
  .word {
    display: inline-block;
    will-change: transform;
    animation: pill-trail var(--dur-pill-enter) var(--ease-standard) both;
    animation-delay: calc(var(--dur-pill-word-delay) + var(--i) * var(--dur-pill-word-stagger));
  }
  @keyframes pill-exit {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(-36px);
      opacity: 0;
    }
  }
  @keyframes pill-slide {
    0% {
      transform: translateX(calc(100% - 34px));
      opacity: 0;
    }
    20% {
      opacity: 1;
    }
    100% {
      transform: translateX(0);
      opacity: 1;
    }
  }
  @keyframes pill-trail {
    from {
      transform: translateX(28px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  @media (max-width: 767px) {
    .propose-trade-button {
      font-size: 0.75rem;
      height: calc(34px + 0.5rem);
      min-width: 9.5rem;
      padding: 0 0.75rem 0 5px;
    }
    .old {
      padding-left: calc(0.75rem - 5px);
    }
  }
</style>
